'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
interface RenameAngleDialogProps {
  angleId: string | null;
  currentName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (angleId: string, newName: string) => Promise<void>;
}

export function RenameAngleDialog({
  angleId,
  currentName,
  open,
  onOpenChange,
  onSave,
}: RenameAngleDialogProps) {
  const t = useTranslations('angle.rename');
  const [name, setName] = useState(currentName || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!angleId) return;
    setIsSaving(true);
    try {
      await onSave(angleId, name.trim());
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to rename angle:', error);
      toast.error(
        error instanceof ApiClientError ? error.getFirstError() : t('errorGeneric')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setName(currentName || '');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('dialogTitle')}</DialogTitle>
          <DialogDescription>{t('dialogDescription')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="angle-name">{t('label')}</Label>
            <Input
              id="angle-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={30}
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t('cancel')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('saving') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
